import { useEffect, useState } from 'react';
import type { RobotStatus } from '../api/types';
import { useLive, type DepthSample, type PeopleSample } from '../live/store';
import { MIN_CONF, filterDepth, trendOf, type SensorCalibration } from './depthFilter';
import { useSensorCalibration } from './sensorCal';

/**
 * §23 front depth summary (RGBD `rgdb_detection`): 6 sectors at ~1 Hz, in cm; readings ≥ maxCm = clear.
 * Sector order is unverified (probably left → right): flip this one constant if the robot turns out to report
 * right → left.
 */
export const DEPTH_LEFT_TO_RIGHT = true;
/** Width of the RGBD fan in degrees (6 × 12°). */
export const FAN_DEG = 72;
/** Robot footprint radius used for TTC (contact at d − 0.25 m). */
export const FOOTPRINT_M = 0.25;
/** Depth older than this → "no data". */
export const STALE_MS = 5000;
/** human_detect threshold (no direction). */
export const HUMAN_RANGE_M = 1.3;

export type ProxState = 'normal' | 'approaching' | 'tooClose' | 'cliff' | 'nodata' | 'charging';
export type Trend = 'approaching' | 'static' | 'receding';

export interface SectorView {
  /** index in the robot's array (0-based) */
  i: number;
  /** display position 0 = leftmost */
  pos: number;
  bearing: number;
  from: number;
  to: number;
  /** filtered meters; null = clear / not near */
  d: number | null;
  /** 0 / negative / missing readings dominate the window */
  nd: boolean;
  /** latest raw reading (cm) */
  raw: number | null;
  /** filtered value in raw cm (median of agreeing readings) */
  filteredCm: number | null;
  /** 0..1 agreement × stability of the current state */
  conf: number;
  /** confident enough for colours-as-object, nearest, tracks and alerts */
  confident: boolean;
  noisy: boolean;
  /** 60 s history: [receive time, raw meters | Infinity (far) | NaN (no data), filtered meters | null] */
  hist: [number, number, number | null][];
}

export interface Track {
  id: string;
  sectors: number[];
  bearing: number;
  d: number;
  trend: Trend;
  /** m/s, ≥ 0 */
  speed: number;
  /** seconds to contact (approaching only) */
  ttc: number | null;
  /** previous positions [d, bearing], oldest first */
  trail: [number, number][];
  conf: number;
  /** depth sectors only / camera face only / both agree */
  source: 'depth' | 'camera' | 'fused';
  kind: 'person' | 'object';
}

export interface SensorView {
  /** robot reports `status.sensors` at all */
  has: boolean;
  fresh: boolean;
  state: ProxState;
  maxM: number;
  sectors: SectorView[];
  tracks: Track[];
  approaching: Track[];
  nearest: number | null;
  human: { near: boolean; at: number | null } | null;
  obstacle: { active: boolean; values: number[]; at: number | null } | null;
  cliff: { active: boolean; values: number[]; at: number | null } | null;
  estop: boolean;
  eskin: string[];
  charging: boolean;
  depthAgeMs: number | null;
  depthRate: number | null;
  calibrated: boolean;
  /** camera people source (head camera face detector) */
  people: { active: boolean; hfovDeg: number; hz: number | null; costMs: number | null; ageMs: number | null; count: number; method: string | null } | null;
}

/** Face detector confidence needed to show a camera person. */
export const PEOPLE_MIN_CONF = 0.4;
/** A camera person and a depth object closer than this (m) along the same bearing are one object. */
const FUSE_M = 0.5;

/** Median-of-3 smoothing for the ~3 Hz camera distance (face size jitters). */
function smooth3(series: { t: number; d: number | null }[]) {
  return series.map((p, k) => {
    if (p.d === null) return p;
    const w = series.slice(Math.max(0, k - 1), k + 2).map((x) => x.d).filter((x): x is number => x !== null).sort((a, b) => a - b);
    return { t: p.t, d: w[w.length >> 1]! };
  });
}

const SW = FAN_DEG / 6;
export const sectorBearing = (pos: number) => -FAN_DEG / 2 + SW * (pos + 0.5);

/** Pure derivation (testable): filtered sectors, tracks from the filtered history, overall proximity state. */
export function deriveSensors(status: RobotStatus | null | undefined, hist: DepthSample[], online: boolean, now: number, cal?: SensorCalibration | null, peopleHist: PeopleSample[] = []): SensorView {
  const s = status?.sensors ?? null;
  const depth = s?.depth ?? null;
  const maxCm = Number(depth?.maxCm) || 120;
  const n = depth?.sectors?.length ?? hist[hist.length - 1]?.sectors.length ?? 6;
  const last = hist[hist.length - 1];
  const depthAgeMs = last ? now - last.t : null;
  const fresh = Boolean(online && depth && last && depthAgeMs !== null && depthAgeMs <= STALE_MS);
  const recent = hist.filter((h) => now - h.t <= 10_000);
  const depthRate = recent.length >= 2 ? (recent.length - 1) / ((recent[recent.length - 1]!.t - recent[0]!.t) / 1000 || 1) : null;
  const ltr = cal ? cal.order === 'ltr' : DEPTH_LEFT_TO_RIGHT;
  const filtered = filterDepth(
    hist.map((h) => h.sectors),
    cal,
  );
  const cur = filtered[filtered.length - 1] ?? [];

  const sectors: SectorView[] = Array.from({ length: n }, (_, i) => {
    const pos = ltr ? i : n - 1 - i;
    const fsec = cur[i];
    const b = sectorBearing(pos);
    const conf = fsec?.conf ?? 0;
    return {
      i,
      pos,
      bearing: b,
      from: b - SW / 2,
      to: b + SW / 2,
      d: fsec?.near ? fsec.d : null,
      nd: fsec ? fsec.nd : true,
      raw: fsec?.raw ?? (typeof depth?.sectors?.[i] === 'number' ? depth.sectors[i]! : null),
      filteredCm: fsec?.filteredCm ?? null,
      conf,
      confident: Boolean(fsec?.near && conf >= MIN_CONF),
      noisy: Boolean(fsec?.noisy),
      hist: hist.map((h, k) => {
        const r = h.sectors[i];
        const rawM = typeof r !== 'number' || r <= 0 ? NaN : r >= (cal?.farCm ?? h.maxCm) ? Infinity : r / 100;
        const fk = filtered[k]?.[i];
        return [h.t, rawM, fk?.near ? fk.d : null] as [number, number, number | null];
      }),
    };
  });

  // Tracks: adjacent CONFIDENT sectors with similar filtered distance form one object; trend from the filtered series.
  const byPos = [...sectors].sort((a, b) => a.pos - b.pos);
  const clusters: SectorView[][] = [];
  for (const sec of byPos) {
    if (!sec.confident || sec.d === null) continue;
    const c = clusters[clusters.length - 1];
    const prev = c?.[c.length - 1];
    if (c && prev && prev.pos === sec.pos - 1 && Math.abs((prev.d ?? 0) - sec.d) <= 0.16) c.push(sec);
    else clusters.push([sec]);
  }
  const useAt = hist.length > 0 && hist.every((h) => typeof h.at === 'number');
  const depthTracks: Track[] = fresh
    ? clusters.map((cl) => {
        const d = Math.min(...cl.map((c) => c.d!));
        const bearing = cl.reduce((sum, c) => sum + c.bearing, 0) / cl.length;
        const series = hist.map((h, k) => {
          const ds = cl.map((c) => filtered[k]?.[c.i]).filter((x) => x?.near).map((x) => x!.d!);
          return { t: useAt ? h.at! : h.t, d: ds.length ? Math.min(...ds) : null };
        });
        const { trend, speed } = trendOf(series);
        const ids = cl.map((c) => c.i + 1);
        return {
          id: `D${ids.length > 1 ? `${ids[0]}–${ids[ids.length - 1]}` : ids[0]}`,
          sectors: cl.map((c) => c.i),
          bearing,
          d,
          trend,
          speed,
          ttc: trend === 'approaching' && speed > 0.01 ? Math.max(0, d - FOOTPRINT_M) / speed : null,
          trail: series
            .slice(-4, -1)
            .filter((p) => p.d !== null)
            .map((p) => [p.d!, bearing] as [number, number]),
          conf: Math.round((cl.reduce((sum, c) => sum + c.conf, 0) / cl.length) * 100) / 100,
          source: 'depth' as const,
          kind: 'object' as const,
        };
      })
    : [];

  // Camera people (bearing + = LEFT on the robot; the radar uses + = right), trend per id, then fusion with depth.
  const ps = s?.peopleSource ?? null;
  const lastP = peopleHist[peopleHist.length - 1];
  const peopleAgeMs = lastP ? now - lastP.t : null;
  // the robot's own flag says whether the detector runs (it needs the camera published); the list is the current one
  const peopleActive = Boolean(online && ps?.active);
  const curPeople = (Array.isArray(s?.people) ? s!.people! : [])
    .filter((p) => p && typeof p.distanceM === 'number' && typeof p.bearingDeg === 'number')
    .map((p) => ({ id: String(p.id), b: p.bearingDeg, d: p.distanceM, c: typeof p.confidence === 'number' ? p.confidence : 1 }));
  const pScale = cal?.peopleScale ?? 1;
  const flip = Boolean(cal?.mirrored && !cal?.mirrorOnRobot);
  const camTracks: Track[] = peopleActive
    ? curPeople
        .filter((p) => p.c >= PEOPLE_MIN_CONF)
        .map((p) => {
          const series = smooth3(
            peopleHist.map((h) => {
              const q = h.people.find((x) => x.id === p.id);
              return { t: q?.at ?? h.t, d: q ? q.d * pScale : null };
            }),
          );
          const { trend, speed } = trendOf(series);
          const d = p.d * pScale;
          const bearing = flip ? p.b : -p.b;
          return {
            id: `P${p.id}`,
            sectors: [] as number[],
            bearing,
            d,
            trend,
            speed,
            ttc: trend === 'approaching' && speed > 0.01 ? Math.max(0, d - FOOTPRINT_M) / speed : null,
            trail: series
              .slice(-10, -1)
              .filter((x, k, a) => x.d !== null && k % 3 === a.length % 3)
              .map((x) => [x.d!, bearing] as [number, number]),
            conf: Math.round(p.c * 100) / 100,
            source: 'camera' as const,
            kind: 'person' as const,
          };
        })
    : [];
  const used = new Set<string>();
  for (const ct of camTracks) {
    const sec = sectors.find((x) => ct.bearing >= x.from && ct.bearing < x.to);
    const dt = depthTracks.find((t) => !used.has(t.id) && sec && t.sectors.includes(sec.i) && Math.abs(t.d - ct.d) <= FUSE_M);
    if (dt) {
      used.add(dt.id);
      ct.source = 'fused';
      ct.sectors = dt.sectors;
      ct.conf = Math.min(1, Math.round((Math.max(ct.conf, dt.conf) + 0.2) * 100) / 100);
    } else if (sec && sec.d !== null && Math.abs(sec.d - ct.d) <= FUSE_M) {
      // a low-confidence depth sector that agrees with the camera: still one (fused) object
      ct.source = 'fused';
      ct.sectors = [sec.i];
      ct.conf = Math.min(1, Math.round((Math.max(ct.conf, sec.conf) + 0.2) * 100) / 100);
    }
  }
  const tracks: Track[] = [...camTracks, ...depthTracks.filter((t) => !used.has(t.id))];
  const approaching = tracks.filter((t) => t.trend === 'approaching');
  const confidentD = [...(fresh ? sectors.filter((x) => x.confident && x.d !== null).map((x) => x.d!) : []), ...camTracks.map((t) => t.d)];
  const nearest = confidentD.length ? Math.min(...confidentD) : null;
  const charging = Boolean(status?.charging || status?.docked);
  const human = s?.human ? { near: Boolean(s.human.near), at: s.human.at ?? null } : null;
  const obstacle = s?.obstacle ? { active: Boolean(s.obstacle.active), values: s.obstacle.values ?? [], at: s.obstacle.at ?? null } : null;
  const cliff = s?.cliff ? { active: Boolean(s.cliff.active), values: s.cliff.values ?? [], at: s.cliff.at ?? null } : null;
  const estop = Boolean(status?.emergencyStop);
  let state: ProxState = 'normal';
  if (charging) state = 'charging';
  else if (online && cliff?.active) state = 'cliff';
  else if (online && (estop || obstacle?.active || (fresh && nearest !== null && nearest < 0.5))) state = 'tooClose';
  else if (!fresh) state = 'nodata';
  else if (approaching.length) state = 'approaching';
  return {
    has: Boolean(s),
    fresh,
    state,
    maxM: maxCm / 100,
    sectors,
    tracks,
    approaching,
    nearest,
    human,
    obstacle,
    cliff,
    estop,
    eskin: s?.eskin?.touched ?? [],
    charging,
    depthAgeMs,
    depthRate,
    calibrated: Boolean(cal),
    people: ps
      ? {
          active: peopleActive,
          hfovDeg: typeof ps.hfovDeg === 'number' && ps.hfovDeg > 0 ? ps.hfovDeg : 60,
          hz: typeof ps.hz === 'number' ? ps.hz : null,
          costMs: typeof ps.costMs === 'number' ? ps.costMs : null,
          ageMs: peopleAgeMs,
          count: camTracks.length,
          method: ps.method ?? null,
        }
      : null,
  };
}

/** Re-render every `ms` (ages, staleness). */
export function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const EMPTY: DepthSample[] = [];
const EMPTY_P: PeopleSample[] = [];

export function useSensors(serial: string) {
  const status = useLive((s) => s.robots[serial]?.status);
  const online = useLive((s) => Boolean(s.robots[serial]?.online));
  const hist = useLive((s) => s.depthHist[serial] ?? EMPTY);
  const ppl = useLive((s) => s.peopleHist[serial] ?? EMPTY_P);
  const now = useNow(1000);
  const cal = useSensorCalibration(serial);
  return deriveSensors(status, hist, online, now, cal, ppl);
}

/** Distance formatter for the m / cm switch. */
export const fmtDist = (m: number, units: 'm' | 'cm', digits = 2) => (units === 'cm' ? `${Math.round(m * 100)} cm` : `${m.toFixed(digits)} m`);
